import { useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Landmark, Loader2, RefreshCw } from "lucide-react";
import {
  useEnableAutoReloadWithMandate,
  isIndiaCard,
  INDIA_AUTO_RELOAD_CAP,
  PHASE_LABEL,
  type WorkspaceBilling,
} from "@/hooks/use-workspace-billing";

/**
 * Armed at the redirect to Stripe Checkout, read on the way back. It has to
 * outlive a full page navigation (`window.location.href`), and the React Query
 * cache does not — on return it is cold, so the billing row cannot tell us
 * whether this checkout is the one that saved the workspace's first card.
 */
const marker = (workspaceId: string) => `auto_reload_promo_pending_${workspaceId}`;
const seenKey = (workspaceId: string) => `auto_reload_promo_seen_${workspaceId}`;

export const armAutoReloadPromo = (workspaceId: string) => {
  try {
    sessionStorage.setItem(marker(workspaceId), "1");
  } catch {
    // Private mode or blocked storage: the promo is optional, never break checkout for it.
  }
};

/** True once, for the checkout that saved the first card. Clears itself. */
export const takeAutoReloadPromo = (workspaceId: string) => {
  try {
    if (!sessionStorage.getItem(marker(workspaceId))) return false;
    sessionStorage.removeItem(marker(workspaceId));
    return !localStorage.getItem(seenKey(workspaceId));
  } catch {
    return false;
  }
};

const rememberAutoReloadPromoSeen = (workspaceId: string) => {
  try {
    localStorage.setItem(seenKey(workspaceId), "1");
  } catch {
    // Nothing to do — worst case the customer sees the offer once more.
  }
};

/** Auto-reload cannot be authorised on an India-issued card of another brand. */
const MANDATE_BRANDS = ["visa", "mastercard"];

export const canOfferAutoReload = (billing: WorkspaceBilling) =>
  billing.payment_method_added &&
  !billing.auto_reload_enabled &&
  (!isIndiaCard(billing) || MANDATE_BRANDS.includes((billing.payment_method_brand ?? "").toLowerCase()));

const money = (value: number) => `$${Number(value).toFixed(0)}`;

export function AutoReloadPromoDialog({
  open,
  onOpenChange,
  billing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  billing: WorkspaceBilling;
}) {
  const enableAutoReload = useEnableAutoReloadWithMandate();
  const india = isIndiaCard(billing);
  const cap = billing.auto_reload_mandate_amount_credits ?? INDIA_AUTO_RELOAD_CAP;
  const phase = enableAutoReload.phase;

  // Stripe mounts the bank's OTP challenge in an iframe on <body>. Radix keeps
  // re-hiding and re-trapping focus away from anything outside an open dialog,
  // which would leave that challenge unusable — so step aside before it opens.
  useEffect(() => {
    if (phase === "verifying") onOpenChange(false);
  }, [phase, onOpenChange]);

  const close = () => {
    rememberAutoReloadPromoSeen(billing.id);
    onOpenChange(false);
  };

  const handleEnable = () => {
    rememberAutoReloadPromoSeen(billing.id);
    // Keep the row's own threshold and amount; only India's bank limit overrides them.
    const clampToCap = india && billing.auto_reload_amount_credits > cap;
    enableAutoReload.mutate(clampToCap ? { auto_reload_amount_credits: cap } : {}, {
      onSettled: () => onOpenChange(false),
    });
  };

  const points = india
    ? [
        "Funds are credited approximately 26 hours after each reload is requested, as required by Reserve Bank of India rules",
        "Reloads are requested in advance, so your balance does not reach zero while funds are in transit",
        "Authorisation requires a one-time password from your bank. No payment is taken now.",
        `Automatic reloads are limited to ${money(cap)} each. Manual top-ups remain immediate and are not subject to this limit.`,
      ]
    : [
        "Calls continue uninterrupted when the balance runs low",
        "Removes the need to monitor your balance or top up manually",
        `Adds ${money(billing.auto_reload_amount_credits)} whenever the balance falls below ${money(billing.auto_reload_threshold_credits)}. Both amounts can be changed at any time.`,
        "Your card is charged only when a reload is triggered, and auto-reload can be turned off at any time",
      ];

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !enableAutoReload.isPending) close(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mb-4">
            {india ? <Landmark className="w-7 h-7 text-primary" /> : <RefreshCw className="w-7 h-7 text-primary" />}
          </div>
          <DialogTitle className="text-xl">
            {india ? "Authorise automatic top-ups" : "Automatic wallet top-ups"}
          </DialogTitle>
          <DialogDescription className="text-base">
            {india
              ? "Cards issued in India require a one-time authorisation from your bank before Verbeo can charge them automatically. We recommend completing this now: each automatic reload settles approximately 26 hours after it is requested, so auto-reload needs to be in place before your balance runs low rather than after."
              : "Your card has been saved. Auto-reload adds credit to your wallet when the balance falls below a level you set, so calls in progress are not interrupted and scheduled campaigns continue without supervision."}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <ul className="p-4 rounded-xl bg-muted/50 space-y-2">
            {points.map((point) => (
              <li key={point} className="flex gap-2 text-sm text-muted-foreground">
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
          {india && (
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Your bank will request a one-time password in the next step. The authorisation becomes
              active a few minutes afterwards.
            </p>
          )}
          {phase && (
            <p className="text-xs text-muted-foreground mt-3 text-center" aria-live="polite">
              {PHASE_LABEL[phase]}
            </p>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={close} disabled={enableAutoReload.isPending} className="w-full sm:w-auto">
            Not now
          </Button>
          <Button onClick={handleEnable} disabled={enableAutoReload.isPending} className="w-full sm:w-auto">
            {enableAutoReload.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {india ? "Authorise with my bank" : "Enable auto-reload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
